import { useAxios } from "@hooks"
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { ExamInstanceTree } from "@types";

interface UpdatePointsParams {
    id: string;
    score?: number;
    answerState?: number;
    examInstanceId?: string;
    /** If set, uses D-Level grading endpoint with validation and recalculations */
    gradingConfigurationId?: string;
}

export const useUpdateExamInstancePoints = () => {

    const axios = useAxios();
    const {t} = useTranslation();
    const {message} = App.useApp();
    const queryClient = useQueryClient();
    

    const updateExamInstancePoints = async (params: UpdatePointsParams) => {
        try {
            // If D-Level grading is enabled, use the grading endpoint for proper validation and recalculations
            if (params.gradingConfigurationId && params.score !== undefined) {
                await axios.put(`/api/grading/examinstancequestions/${params.id}/score`, params);
            } else {
                // Standard points update
                await axios.patch(`/api/ExamInstanceQuestion/UpdatePoints/${params.id}`, params);
            }
        } catch (error: any) {
            // Extract error message from response if available
            const errorMessage = error?.response?.data?.error || t("messages.error.updateExamInstancePoints");
            throw new Error(errorMessage);
        }
    }

    return useMutation({
        mutationFn: updateExamInstancePoints, 
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ["examInstanceQuestion", variables.id]});
            // Directly update the tree cache so sidebar graded/not-graded indicators update immediately
            // (avoids race condition with server refetch timing)
            if (variables.examInstanceId) {
                queryClient.setQueryData<ExamInstanceTree>(
                    ["examInstanceTree", variables.examInstanceId],
                    (oldData) => {
                        if (!oldData) return oldData;
                        return {
                            ...oldData,
                            groups: oldData.groups.map(group => ({
                                ...group,
                                questions: group.questions.map(q =>
                                    q.id === variables.id
                                        ? { ...q, points: variables.score ?? q.points, state: variables.answerState ?? q.state }
                                        : q
                                )
                            }))
                        };
                    }
                );
            }
            // Also invalidate grading summary if D-Level grading
            if (variables.gradingConfigurationId && variables.examInstanceId) {
                queryClient.invalidateQueries({queryKey: ["gradingSummary", variables.examInstanceId]});
            }
            message.success(t("messages.success.updateExamInstancePoints"));
        },
        onError: (error: Error) => {
            message.error(error.message);
        }
    })
}